import React, { ReactNode, useRef } from 'react';
import { useRBAC } from '../../RbacProvider';
import { showTooltip, hideTooltip } from '../../../components/common/simple-tool-tip/TooltipManager';
import './ProtectAction.scss';

interface ProtectActionProps {
    resource?: string;
    action?: string;
    children: ReactNode;
    tooltipMessage?: string;
    disable?: boolean;
    className?: string;
    canAccess?: boolean;
}

export const ProtectAction: React.FC<ProtectActionProps> = ({
    resource,
    action,
    children,
    tooltipMessage = 'Contact your admin to upgrade and use this feature.',
    disable = false,
    className = '',
    canAccess = false,
}) => {
    const wrapperRef = useRef<HTMLElement>(null);

    // Create a callback ref to handle different element types
    const setRef = (node: HTMLElement | null) => {
        (wrapperRef as any).current = node;
    };

    if (disable) {
        return <>{children}</>;
    }

    const { can, loading } = useRBAC();

    // If still loading permissions, render children normally
    if (loading) {
        return <>{children}</>;
    }

    let hasAccess = canAccess;
    if (resource && action) {
        // Check if the user has permission to perform this action
        hasAccess = can(resource, action);
    }

    const handleMouseEnter = () => {
        if (!hasAccess && wrapperRef.current) {
            showTooltip(tooltipMessage, wrapperRef.current);
        }
    };

    const handleMouseLeave = () => {
        if (!hasAccess) {
            hideTooltip();
        }
    };

    const handleClick = (event: React.MouseEvent) => {
        if (!hasAccess) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    // If user has access, render children normally
    if (hasAccess) {
        return <>{children}</>;
    }

    // Ensure children is a valid React element
    if (!React.isValidElement(children)) {
        console.warn('ProtectAction: children must be a valid React element');
        return <>{children}</>;
    }

    // User doesn't have access - clone the element and add disabled props
    const element = children as React.ReactElement;
    return React.cloneElement(element, {
        ref: setRef,
        className: `${element.props.className || ''} protect-action-wrapper ${className}`.trim(),
        // Remove any existing tooltip attributes to prevent conflicts
        title: undefined,
        'data-tooltip': undefined,
        'data-title': undefined,
        onMouseEnter: (e: React.MouseEvent) => {
            // Prevent any default tooltip behavior
            e.preventDefault();
            e.stopPropagation();
            handleMouseEnter();
        },
        onMouseLeave: (e: React.MouseEvent) => {
            // Prevent any default tooltip behavior
            e.preventDefault();
            e.stopPropagation();
            handleMouseLeave();
        },
        onClick: (e: React.MouseEvent) => {
            handleClick(e);
            // Don't call original onClick when access is denied
        },
        'aria-disabled': true,
        style: {
            ...element.props.style,
            cursor: 'not-allowed',
            opacity: 0.4,
            filter: 'grayscale(80%)'
        }
    });
};